/* WeQuote Dev Board — constants. Ported verbatim from the prototype (app.js).
 * The DEV ROSTER is dynamic on the platform: setDevRoster() is called with the
 * real agents (is_dev) once the board loads, replacing the prototype's five
 * hardcoded developers. Until then the prototype roster applies, so imported/
 * legacy data and the behaviour tests keep their exact semantics. */
import type { Effort, Priority } from './types'

export const STORAGE_KEY = 'wequote-dev-board-v2'

/* ── time estimates (working days) ── */
export const TIME_OPTS: { val: string; label: string }[] = [
  { val: '', label: 'TBD' },
  { val: '0.1', label: 'Under an hour' },
  { val: '0.25', label: 'A couple of hours' },
  { val: '0.5', label: 'Half a day' },
  { val: '1', label: '1 day' },
  { val: '2', label: '2 days' },
  { val: '3', label: '3 days' },
  { val: '4', label: '4 days' },
  { val: '5', label: '1 week' },
  { val: '10', label: '2 weeks' },
  { val: '15', label: '3 weeks' },
  { val: '20', label: '1 month' },
  { val: '40', label: '1–2 months' },
]
export const TIME_MAP: Record<string, string> = {}
TIME_OPTS.forEach((o) => { TIME_MAP[o.val] = o.label })

export function timeLabel(val: string): string {
  if (Object.prototype.hasOwnProperty.call(TIME_MAP, val)) return TIME_MAP[val]
  const d = parseFloat(val)
  if (isNaN(d)) return val || ''
  return d === 1 ? '1 day' : d + ' days'
}

/** Effort tier is ALWAYS derived from the working-day estimate (PRD §5.3). */
export function calcEffort(val: string): Effort | null {
  const d = parseFloat(val)
  if (!val || isNaN(d)) return null
  if (d < 0.5) return 'quickwin' // less than half a day — a "quick win"
  if (d <= 2) return 'low'
  if (d <= 5) return 'medium'
  return 'high'
}

/* ── WeQuote modules (Lee, 28 Aug): which part of the product a card is for.
 * Required from the moment a card enters the board (Needs Review / Ideas);
 * blank only on CSV-imported cards. */
export const MODULE_OPTS: string[] = [
  'CRM',
  'Quoting & Proposals',
  'Accounting',
  'Project Management & Snagg',
  'Product Log & Procurement',
  'Inventory',
  'Other',
  'Internal Functionality',
]

/* ── labels ── */
export const PL: Record<Priority, string> = { high: 'High priority', medium: 'Medium priority', low: 'Low priority' }
export const EL: Record<Effort, string> = { high: 'High effort', medium: 'Med effort', low: 'Low effort', quickwin: 'Quick win' }
export const CUST_OPTS: { v: string; l: string }[] = [
  { v: '', l: 'Automatic (from requests)' },
  { v: 'All', l: 'All' },
  { v: 'Internal request', l: 'Internal request' },
  { v: '2+ companies', l: '2+ companies' },
  { v: '10+ companies', l: '10+ companies' },
]

/* ── the dev roster (dynamic on the platform) ── */
export interface RosterDev { id: string; label: string; color: string }

const PROTO_ROSTER: RosterDev[] = [
  { id: 'robert', label: 'Robert', color: '#2563eb' },
  { id: 'yadhu', label: 'Yadhu', color: '#10b981' },
  { id: 'rizwan', label: 'Rizwan', color: '#f59e0b' },
  { id: 'sreya', label: 'Sreya', color: '#8b5cf6' },
  { id: 'leenithish', label: 'Lee/Nithish', color: '#ec4899' },
]
const ROSTER_PALETTE = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#0ea5e9', '#f43f5e', '#84cc16']

export let DEVCOLS: string[] = PROTO_ROSTER.map((d) => d.id)
export let DL: Record<string, string> = Object.fromEntries(PROTO_ROSTER.map((d) => [d.id, d.label]))
export let DEV_COLORS: Record<string, string> = {
  ...Object.fromEntries(PROTO_ROSTER.map((d) => [d.id, d.color])),
  unassigned: '#9ca3af',
}

/** Swap in the real roster (agents where is_dev). A dev's board id is the
 *  EMAIL LOCAL PART (rb, yk, …) — stable and human-readable; card data written
 *  against it survives DB row-id changes. Falls back to `agent-<id>`. */
export function setDevRoster(devs: { id: number | string; name: string; email?: string }[]): void {
  if (!devs || !devs.length) return
  const roster: RosterDev[] = devs.map((d, i) => ({
    id: d.email && d.email.indexOf('@') > 0 ? d.email.split('@')[0].toLowerCase() : 'agent-' + d.id,
    label: d.name,
    color: ROSTER_PALETTE[i % ROSTER_PALETTE.length],
  }))
  DEVCOLS = roster.map((d) => d.id)
  DL = Object.fromEntries(roster.map((d) => [d.id, d.label]))
  DEV_COLORS = { ...Object.fromEntries(roster.map((d) => [d.id, d.color])), unassigned: '#9ca3af' }
}

export function devColor(k: string): string {
  return DEV_COLORS[k] || '#9ca3af'
}

/* ── statuses / columns (built from the roster so lanes are real users) ── */
export function statusLabels(): Record<string, string> {
  const m: Record<string, string> = {
    needsreview: 'Needs Review',
    later: 'Later',   // the raw pool (v2 §1 — replaces Backlog; legacy alias kept in isPool)
    next: 'Next',     // the shortlist
    now: 'Now',       // imminent
    design: 'Design in Progress',
    designcomplete: 'Design Complete',
  }
  DEVCOLS.forEach((d) => { m[d] = 'Dev: ' + DL[d] })
  m.custreview = 'Waiting on Customer Review' // after dev, before testing (Lee, 29 Aug)
  m.testing = 'Ready to Test'
  m.testcomplete = 'Testing Complete'
  m.live = 'Live'
  m.onhold = 'On Hold'
  m.archived = 'Archived'
  return m
}
export function statusClasses(): Record<string, string> {
  const m: Record<string, string> = {}
  Object.keys(statusLabels()).forEach((k) => { m[k] = 's-' + (DEVCOLS.indexOf(k) > -1 ? 'dev' : k) })
  return m
}
export function kanbanCols(): { id: string; label: string; cls: string }[] {
  const L = statusLabels()
  return Object.keys(L).map((id) => ({ id, label: L[id], cls: 'col-' + (DEVCOLS.indexOf(id) > -1 ? 'dev' : id) }))
}

/* ── status predicates (verbatim behaviour) ── */
export function isProgress(s: string): boolean {
  return DEVCOLS.indexOf(s) > -1 || s === 'custreview' || s === 'testing' || s === 'testcomplete' || s === 'onhold' || s === 'live'
}
export function pastDesign(s: string): boolean {
  return DEVCOLS.indexOf(s) > -1 || s === 'custreview' || s === 'testing' || s === 'testcomplete'
}
/* ── pool bands (v2 §1) ── */
export const POOLCOLS: string[] = ['later', 'next', 'now']
export function isPool(s: string): boolean { return POOLCOLS.indexOf(s) > -1 || s === 'backlog' }

export function isReview(s: string): boolean { return s === 'needsreview' }
export function isArchived(s: string): boolean { return s === 'archived' }
/** In the priority pipeline = ranked + on the Priority List (excludes live, review, archived, ideas). */
export function inPipeline(f: { status: string }): boolean {
  return f.status !== 'live' && f.status !== 'needsreview' && f.status !== 'archived' && f.status !== 'idea'
}

/** Status label lookup against the CURRENT roster-built label map. */
export function SLGet(status: string): string {
  return statusLabels()[status] || status
}

/* ── Roadmap layout (verbatim) ── */
export const RM_CW = 176
export const RM_CWC = 104
export const RM_GAP = 10
export const RM_MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const RM_MON_COLORS = ['#dbeafe', '#e0e7ff', '#ede9fe', '#fae8ff', '#fce7f3', '#ffe4e6', '#ffedd5', '#fef9c3', '#ecfccb', '#dcfce7', '#ccfbf1', '#cffafe']
